import { OptionallyVisible } from 'components';
import { Trans, useTranslation } from 'react-i18next';
import { MerchantMemoResponse } from 'services/merchants/types';
import { useGetRefundMemoQuery } from 'services/refunds';

import { Delimiter } from 'components/delimiter';
import IconExclamationCircled from 'components/icons/exclamationCircled';
import IconExclamationCircledFilled from 'components/icons/exclamationCircledFilled';
import LoadingIndicator from 'components/loadingIndicator/loadingIndicator';

import { ReduxToolkitError } from 'types/http';

import { LABELS } from './keys';
import { ErrorInfo, ErrorText, ExclamationSection, Field, Label, ModalContent, Section, ThemedSection, Value } from './styles';

interface TopUpModalContentProps {
  merchantUuid: string;
  insufficientBalance?: boolean;
  insufficientBalanceMessage?: string;
}

const hasText = (value?: string | null): value is string => Boolean(value?.trim());

const MemoField = ({ label, value }: { label: string; value?: string | null }) => {
  if (!hasText(value)) {
    return null;
  }

  return (
    <Field>
      <Label>{label}</Label>
      <Value>{value}</Value>
    </Field>
  );
};

const hasPrimaryTopUpFields = (memo?: MerchantMemoResponse): boolean =>
  Boolean(
    memo &&
      [
        memo.brCode,
        memo.iban,
        memo.clabe,
        memo.breBKey,
        memo.bankAccountNumber,
        memo.bankRoutingNumber,
        memo.depositMessage,
        memo.accountHolderName,
      ].some(hasText),
  );

const hasBankDetails = (memo?: MerchantMemoResponse): boolean =>
  Boolean(
    memo &&
      [
        memo.bankName,
        memo.bankAddress,
        memo.bankBeneficiaryName,
        memo.bankBeneficiaryAddress,
        memo.bankAccountNumber,
        memo.bankRoutingNumber,
      ].some(hasText),
  );

const shouldShowBeneficiaryName = (memo?: MerchantMemoResponse): boolean => {
  if (!hasText(memo?.bankBeneficiaryName)) {
    return false;
  }

  return memo.bankBeneficiaryName.trim() !== memo.accountHolderName?.trim();
};

const shouldShowAccountNumber = (memo?: MerchantMemoResponse): boolean => {
  if (!hasText(memo?.bankAccountNumber)) {
    return false;
  }

  return memo.bankAccountNumber.trim() !== memo.breBKey?.trim();
};

export const TopupModalContent = ({ merchantUuid, insufficientBalance, insufficientBalanceMessage }: TopUpModalContentProps) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetRefundMemoQuery({ uuid: merchantUuid }, { skip: !merchantUuid });
  const paymentRails = data?.paymentRails?.map((rail) => rail.replaceAll('_', ' ').toUpperCase()).join(', ');
  const hasTransferMemo = hasText(data?.depositMessage);
  const showPrimarySection = hasPrimaryTopUpFields(data);

  const getContent = () => {
    if (isLoading) {
      return <LoadingIndicator padded />;
    }

    if (error) {
      return <ErrorText>{(error as ReduxToolkitError)?.data?.errorMessage ?? t(LABELS.GENERIC_ERROR)}</ErrorText>;
    }

    return (
      <>
        <OptionallyVisible visible={insufficientBalance}>
          <ErrorInfo>
            <IconExclamationCircled width={38} height={38} />
            <h3>{t(LABELS.INSUFFICIENT_BALANCE_TITLE)}</h3>
            <p>
              {insufficientBalanceMessage ?? (
                <Trans
                  i18nKey={LABELS.INSUFFICIENT_BALANCE_MESSAGE[0]}
                  defaults={LABELS.INSUFFICIENT_BALANCE_MESSAGE[1]}
                  components={{ br: <br /> }}
                />
              )}
            </p>
            <Delimiter />
          </ErrorInfo>
        </OptionallyVisible>
        <ExclamationSection>
          <div>
            <IconExclamationCircledFilled />
          </div>
          <div>
            <Trans
              i18nKey={(paymentRails ? LABELS.IMPORTANT_SECTION.NOTE_1 : LABELS.IMPORTANT_SECTION.NOTE_1_FALLBACK)[0]}
              defaults={(paymentRails ? LABELS.IMPORTANT_SECTION.NOTE_1 : LABELS.IMPORTANT_SECTION.NOTE_1_FALLBACK)[1]}
              values={{ paymentRails }}
              components={{ b: <strong /> }}
            />
          </div>
        </ExclamationSection>
        <OptionallyVisible visible={hasTransferMemo}>
          <ExclamationSection>
            <div>
              <IconExclamationCircledFilled />
            </div>
            <div>
              <Trans
                i18nKey={LABELS.IMPORTANT_SECTION.NOTE_2[0]}
                defaults={LABELS.IMPORTANT_SECTION.NOTE_2[1]}
                components={{ b: <strong /> }}
              />
            </div>
          </ExclamationSection>
        </OptionallyVisible>
        <OptionallyVisible visible={showPrimarySection}>
          <ThemedSection>
            <MemoField label={t(LABELS.FIELDS.BR_CODE)} value={data?.brCode} />
            <MemoField label={t(LABELS.FIELDS.IBAN)} value={data?.iban} />
            <MemoField label={t(LABELS.FIELDS.CLABE)} value={data?.clabe} />
            <MemoField label={t(LABELS.FIELDS.BRE_B_KEY)} value={data?.breBKey} />
            {shouldShowAccountNumber(data) ? <MemoField label={t(LABELS.FIELDS.ACCOUNT_NUMBER)} value={data?.bankAccountNumber} /> : null}
            <MemoField label={t(LABELS.FIELDS.ROUTING_NUMBER)} value={data?.bankRoutingNumber} />
            <MemoField label={t(LABELS.FIELDS.TRANSFER_MEMO)} value={data?.depositMessage} />
            <MemoField label={t(LABELS.FIELDS.ACCOUNT_HOLDER_NAME)} value={data?.accountHolderName} />
          </ThemedSection>
        </OptionallyVisible>
        <OptionallyVisible visible={hasBankDetails(data)}>
          <Section>
            <MemoField label={t(LABELS.FIELDS.BANK_NAME)} value={data?.bankName} />
            <MemoField label={t(LABELS.FIELDS.BANK_ADDRESS)} value={data?.bankAddress} />
            {shouldShowBeneficiaryName(data) ? (
              <MemoField label={t(LABELS.FIELDS.BENEFICIARY_NAME)} value={data?.bankBeneficiaryName} />
            ) : null}
            <MemoField label={t(LABELS.FIELDS.BENEFICIARY_ADDRESS)} value={data?.bankBeneficiaryAddress} />
          </Section>
        </OptionallyVisible>
      </>
    );
  };

  return <ModalContent>{getContent()}</ModalContent>;
};
